/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    takeLatest, call, put, fork, select, cancel,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';
import jwtDecode from 'jwt-decode';
// apis
import { createEnterpriseIdRestCall } from '../../../../../apis/enterpriseid-apis/createEnterpriseIdRestCall';
import { updateIdentifiersEnterpriseIdRestCall } from '../../../../../apis/enterpriseid-apis/updateIdentifiersEnterpriseIdRestCall';
// redux
import * as helper from '../Auth/helper/helper';
import * as enterpriseIdActions from './EnterpriseId-Actions';
import { logUserLoggedInSuccess } from '../Auth/Auth-Actions';
import { getIsBot } from '../../../App/App-Selectors';
import { getFeatureFlag } from '../../../App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomerIdentificationEvent } from '../../../TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getCurrentBrandId } from '../../../App/ducks/Brand/Brand-Selectors';
import { uuidv4 } from '../../../../../app/helpers/uuid';
import { checkJWT } from '../Auth/Auth-Operations';
import { setTokenLocalStorage, removeNullFromIdentifiers, getEmailMarketingId } from './EnterpriseId-Helpers';
import { getBillingAddress } from '../../../MfeCheckout/ducks/Cart/Cart-Selectors';

function* generateCustomerIdentifiers(payload) {
    // if no uuid probably new customer, generate and add to EID call for single identifier if rest are null
    let browserUUID = yield call(helper.getTokenLocalStorage, 'browserUUID');
    if (!browserUUID) {
        browserUUID = yield call(uuidv4);
        setTokenLocalStorage(browserUUID, 'browserUUID');
    }
    // global identifiers
    const jwtToken = yield call(checkJWT);
    const billingInformation = yield select(getBillingAddress);
    const brandId = yield select(getCurrentBrandId);
    const emailMarketingId = getEmailMarketingId(payload);

    // action specific identifiers
    let emailId;
    let contactId;
    let auth0UserId;
    if (payload?.data) {
        contactId = payload.data?.user.contactId;
        emailId = payload.data?.user.email;
        const jwtPayload = jwtDecode(jwtToken);
        auth0UserId = jwtPayload?.sub;
    }

    // prepare API payload
    const identifiers = {
        brandId,
        identifiers: removeNullFromIdentifiers([
            billingInformation.email && { key: 'billingEmail', value: `${billingInformation.email}` }, // billing form
            // TODO add phone number from billing form
            browserUUID && { key: 'browserUUID', value: `${browserUUID}` }, // generated or cookie
            emailMarketingId && { key: 'emailMarketingId', value: `${emailMarketingId}` }, // comes from url
            contactId && { key: 'contactId', value: `${contactId}` }, // jwtToken
            emailId && { key: 'emailId', value: `${emailId}` }, // jwtToken
            auth0UserId && { key: 'auth0UserId', value: `${auth0UserId}` }, // jwtToken
        ]),
    };

    return { identifiers, jwtToken };
}

function* handleEIDResponse(response) {
    yield put(enterpriseIdActions.enterpriseIdLoaded(response.enterpriseId));
    setTokenLocalStorage(response.enterpriseId, 'enterpriseId');
    if (typeof window !== 'undefined' && window.mbpDataLayerView && window.utag?.data && response.enterpriseId) {
        window.mbpDataLayerView.customer_eid = response.enterpriseId || '';
        window.utag.data.customer_eid = response.enterpriseId || '';
    }
}

export function* workerSagaCreateEnterpiseId(payload) {
    try {
        yield put(enterpriseIdActions.enterpriseIdLoading());
        // Do not assign EID to bots
        const isBot = yield select(getIsBot);
        if (isBot) {
            return;
        }

        // Try local storage value
        const localStorageValueOfEnterpriseId = yield call(helper.getTokenLocalStorage, 'enterpriseId');
        if (localStorageValueOfEnterpriseId) {
            yield put(enterpriseIdActions.enterpriseIdLoaded(localStorageValueOfEnterpriseId));

            // Force Fix Flow (TODO remove)
            const isForceFixEnabled = yield select(getFeatureFlag('is-eid-force-fix-enabled'));
            let fixedEIDResponse;
            if (isForceFixEnabled) {
                try {
                    const { identifiers, jwtToken } = yield call(generateCustomerIdentifiers, payload);
                    fixedEIDResponse = yield call(updateIdentifiersEnterpriseIdRestCall, jwtToken, identifiers, localStorageValueOfEnterpriseId);
                    if (fixedEIDResponse.enterpriseId !== localStorageValueOfEnterpriseId) {
                        yield call(handleEIDResponse, fixedEIDResponse);
                        // allows Qubit to synchronize changes on their side w/ their tracker
                        yield put(trackEvent({
                            eventCategory: 'Enterprise Id',
                            eventAction: 'EID Force Fix',
                            eventLabel: 'Customer EID updated',
                        }));
                        document.dispatchEvent(new CustomEvent('eidForceCorrected', { detail: { enterpriseId: fixedEIDResponse.enterpriseId } }));
                    }
                    yield put(emitCustomerIdentificationEvent({ eventType: 'clickstream.addEidMetaData', eidSource: 'api' }));
                    return;
                } catch (ex) {
                    mbpLogger.logError({
                        function: 'workerSagaCreateEnterpiseId -> Force Fix',
                        appName: process.env.npm_package_name,
                        module: 'EnterpriseId',
                        message: 'Failed to load EID from PATCH route during force fix',
                        jsError: ex,
                    });
                }
            }
            yield put(emitCustomerIdentificationEvent({ eventType: 'clickstream.addEidMetaData', eidSource: 'local' }));
            return;
        }

        // flag enabled - won't call eid service, as it's deprecated and not needed for 5.0
        const isEidDisabled = yield select(getFeatureFlag('is-turn-off-eid-service-enabled'));
        if (isEidDisabled) {
            return;
        }

        // Call API, no token in local
        try {
            const { identifiers, jwtToken } = yield call(generateCustomerIdentifiers, payload);
            const response = yield call(createEnterpriseIdRestCall, jwtToken, identifiers);

            if (response?.enterpriseId) {
                yield call(handleEIDResponse, response);
                yield put(emitCustomerIdentificationEvent({ eventType: 'clickstream.addEidMetaData', eidSource: 'api' }));
            }
        } catch (ex) {
            mbpLogger.logError({
                function: 'workerSagaCreateEnterpiseId -> POST for new EID',
                appName: process.env.npm_package_name,
                module: 'EnterpriseId',
                message: 'Failed to load EID from POST route creation',
                jsError: ex,
            });
        }
    } catch (ex) {
        mbpLogger.logError({
            function: 'workerSagaCreateEnterpiseId -> Unhandled Error',
            appName: process.env.npm_package_name,
            module: 'EnterpriseId',
            message: 'Failed to execute saga',
            jsError: ex,
        });
    }
}

function* watcherCreateEnterpiseId() {
    yield takeLatest(enterpriseIdActions.requestEnterpriseId().type, workerSagaCreateEnterpiseId);
}

export function* workerSagaUpdateEnterpriseId(payload) {
    try {
        yield put(enterpriseIdActions.enterpriseIdLoading());
        // Do not allow bots to update EID
        const isBot = yield select(getIsBot);
        if (isBot) {
            yield cancel();
        }
        // Update localStorage value from PATCH route
        const localStorageValueOfEnterpriseId = yield call(helper.getTokenLocalStorage, 'enterpriseId');
        if (localStorageValueOfEnterpriseId) {
            try {
                const { identifiers, jwtToken } = yield call(generateCustomerIdentifiers, payload);
                const response = yield call(updateIdentifiersEnterpriseIdRestCall, jwtToken, identifiers, localStorageValueOfEnterpriseId);

                if (response?.enterpriseId) {
                    yield call(handleEIDResponse, response);
                }
            } catch (ex) {
                mbpLogger.logError({
                    function: 'workerSagaUpdateEnterpriseId -> Error updating EID',
                    appName: process.env.npm_package_name,
                    module: 'EnterpriseId',
                    message: 'Failed to update EID',
                    jsError: ex,
                });
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            function: 'workerSagaUpdateEnterpriseId -> Unhandled Error',
            appName: process.env.npm_package_name,
            module: 'EnterpriseId',
            message: 'Failed to execute saga',
            jsError: ex,
        });
    }
}

export function* watcherSagaUpdateEnterpriseId() {
    yield takeLatest([
        // sign in events
        enterpriseIdActions.updateOnSignInEnterpriseId().type,
        logUserLoggedInSuccess().type,
        // order events
        enterpriseIdActions.requestToUpdateEnterpiseId().type,
    ], workerSagaUpdateEnterpriseId);
}

const watchers = [
    fork(watcherCreateEnterpiseId),
    fork(watcherSagaUpdateEnterpriseId),
];

const workers = {
    workerSagaUpdateEnterpriseId,
    workerSagaCreateEnterpiseId,
};

export default {
    watchers,
    workers,
};
